import { CircleOff, Crosshair, Hand, Minus, Plus } from "lucide-react";
import type {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useStore } from "../app/store.ts";
import { CameraPanel } from "../ui/CameraPanel.tsx";
import { ControlGroupSeparator } from "../ui/ControlGroupSeparator.tsx";
import { EDGE_PALETTE_WIDTH, EdgePalette } from "../ui/EdgePalette.tsx";
import { CameraIcon, NavigateIcon, PrecisionIcon } from "../ui/Icons.tsx";
import { Tool } from "../ui/tool/Tool.tsx";
import type { ToolVariant } from "../ui/tool/ToolFlyout.tsx";
import { ToolSurface } from "../ui/tool/ToolSurface.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_BORDER,
  COLOR_HEADER_BG,
  COLOR_TEXT_FAINT,
  COLOR_WARNING,
  TOOL_FLYOUT_ICON_SIZE,
  TOOL_ICON_SIZE,
} from "../ui/tokens.ts";

const TRACK_SECTION_HEIGHT = 116;
const TRACK_HEIGHT = 74;
const TRACK_WIDTH = 2;
const PUCK_SIZE = 28;
const PUCK_TRAVEL = 28;
const TAP_THRESHOLD = 6;
const DRAG_REST_DEADZONE = 0.12;
const DRAG_RESPONSE_EXPONENT = 2.35;
const ZOOM_FRAME_STEP = 1 / 60;
const ZOOM_FRAME_BASE = 0.985;
const PALETTE_BUTTON_GAP = 6;
const PALETTE_BOTTOM_PADDING = 8;
const CAMERA_PALETTE_STORAGE_KEY = "poseref-viewport-camera-palette-offset";

const PRECISION_LEVELS = [
  { label: "Off", value: 0, icon: CircleOff },
  { label: "\u00BD Precision", value: 0.5, icon: Crosshair },
  { label: "\u00BC Precision", value: 0.25, icon: Crosshair },
] as const;

const PRECISION_LABELS: Record<number, string> = {
  0.5: "\u00BD",
  0.25: "\u00BC",
};

const NAV_BLOCKED_ANIM = "nav-blocked-flash";
const NAV_BLOCKED_DURATION = 600;

const _offset = new THREE.Vector3();

let navBlockedStyleInjected = false;

function ensureNavBlockedStyle() {
  if (navBlockedStyleInjected) return;
  const style = document.createElement("style");
  style.textContent = `@keyframes ${NAV_BLOCKED_ANIM} {
  0%   { box-shadow: 0 0 0 0 ${COLOR_WARNING}66; }
  30%  { box-shadow: 0 0 8px 2px ${COLOR_WARNING}88; }
  100% { box-shadow: 0 0 0 0 ${COLOR_WARNING}00; }
}`;
  document.head.appendChild(style);
  navBlockedStyleInjected = true;
}

function applyZoomDelta(
  speed: number,
  controls: OrbitControlsImpl,
  dtSeconds: number,
): void {
  const state = useStore.getState();
  const magnitude = Math.abs(speed);
  if (magnitude < 0.01) return;

  const factor =
    ZOOM_FRAME_BASE **
    ((dtSeconds / ZOOM_FRAME_STEP) *
      Math.max(0.5, controls.zoomSpeed) *
      (0.08 + magnitude * 4.92));
  const zoomIn = speed > 0;

  if (state.cameraProjection === "orthographic") {
    state.setOrthographicHalfHeight(
      state.orthographicHalfHeight * (zoomIn ? factor : 1 / factor),
    );
    return;
  }

  _offset.subVectors(controls.object.position, controls.target);
  _offset.multiplyScalar(zoomIn ? factor : 1 / factor);
  const radius = _offset.length();
  if (radius < 0.1 || radius > 100) {
    _offset.setLength(Math.min(100, Math.max(0.1, radius)));
  }
  controls.object.position.copy(controls.target).add(_offset);
  controls.update();
}

function speedFromDragOffset(offsetPx: number): number {
  const normalized = -offsetPx / PUCK_TRAVEL;
  const magnitude = Math.abs(normalized);
  if (magnitude <= DRAG_REST_DEADZONE) return 0;
  const active = (magnitude - DRAG_REST_DEADZONE) / (1 - DRAG_REST_DEADZONE);
  return Math.sign(normalized) * active ** DRAG_RESPONSE_EXPONENT;
}

function useNavBlockedFlash(): number {
  const [flashKey, setFlashKey] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = () => {
      if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
      setFlashKey((key) => key + 1);
      timeoutRef.current = setTimeout(
        () => setFlashKey(0),
        NAV_BLOCKED_DURATION,
      );
    };

    window.addEventListener("nav-blocked", handler);
    return () => {
      window.removeEventListener("nav-blocked", handler);
      if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    };
  }, []);

  return flashKey;
}

function PrecisionControlButton() {
  const precisionLevel = useStore((s) => s.precisionLevel);
  const setPrecisionLevel = useStore((s) => s.setPrecisionLevel);
  const isActive = precisionLevel > 0;

  const variants: ToolVariant[] = PRECISION_LEVELS.map((level) => {
    const Icon = level.icon;
    return {
      key: String(level.value),
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE - 1} />,
      label: level.label,
      selected: level.value === precisionLevel,
      onActivate: () => setPrecisionLevel(level.value),
    };
  });

  return (
    <Tool
      icon={<PrecisionIcon size={TOOL_ICON_SIZE} />}
      tip="Precision mode"
      active={isActive}
      badges={isActive ? [PRECISION_LABELS[precisionLevel]] : undefined}
      variants={variants}
      flyoutMinWidth={160}
    />
  );
}

function CameraControlButton() {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tool
        ref={buttonRef}
        icon={<CameraIcon size={TOOL_ICON_SIZE} />}
        tip="Camera"
        active={open}
        onActivate={() => setOpen((prev) => !prev)}
      />
      <CameraPanel
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function ViewportCameraControls({
  controlsRef,
  syncRef,
}: {
  controlsRef: RefObject<OrbitControlsImpl | null>;
  syncRef: RefObject<() => void>;
}) {
  const chromeHidden = useStore((s) => s.chromeHidden);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const orbitEnabled = useStore((s) => s.orbitEnabled);
  const spaceOrbitActive = useStore((s) => s.spaceOrbitActive);
  const setOrbitEnabled = useStore((s) => s.setOrbitEnabled);
  const singleDragPanEnabled = useStore((s) => s.singleDragPanEnabled);
  const setSingleDragPanEnabled = useStore((s) => s.setSingleDragPanEnabled);

  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const zoomFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const speedRef = useRef(0);
  const originYRef = useRef(0);
  const movedRef = useRef(false);
  const tapEligibleRef = useRef(false);
  const navButtonRef = useRef<HTMLButtonElement>(null);

  const showNavigationControls = workspaceMode !== "render";
  const navFlashKey = useNavBlockedFlash();

  useEffect(() => {
    const el = navButtonRef.current;
    if (!el || navFlashKey === 0 || !showNavigationControls) return;
    ensureNavBlockedStyle();
    const prevBorder = el.style.borderColor;
    const prevColor = el.style.color;
    const prevAnim = el.style.animation;
    el.style.animation = `${NAV_BLOCKED_ANIM} ${NAV_BLOCKED_DURATION}ms ease-out`;
    el.style.borderColor = COLOR_WARNING;
    el.style.color = COLOR_WARNING;
    const restore = () => {
      el.style.animation = prevAnim;
      el.style.borderColor = prevBorder;
      el.style.color = prevColor;
    };
    el.addEventListener("animationend", restore, { once: true });
    return () => {
      el.removeEventListener("animationend", restore);
      restore();
    };
  }, [navFlashKey, showNavigationControls]);

  const stopZoomLoop = useCallback(() => {
    if (zoomFrameRef.current != null) {
      cancelAnimationFrame(zoomFrameRef.current);
      zoomFrameRef.current = null;
    }
    lastFrameTimeRef.current = null;
    speedRef.current = 0;
  }, []);

  const finishZoomGesture = useCallback(() => {
    stopZoomLoop();
  }, [stopZoomLoop]);

  const beginZoomLoop = useCallback(() => {
    if (zoomFrameRef.current != null) return;

    const step = (now: number) => {
      const controls = controlsRef.current;
      if (!controls) {
        stopZoomLoop();
        return;
      }
      const last = lastFrameTimeRef.current ?? now;
      const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
      lastFrameTimeRef.current = now;
      applyZoomDelta(speedRef.current, controls, dt);
      syncRef.current?.();
      zoomFrameRef.current = requestAnimationFrame(step);
    };

    zoomFrameRef.current = requestAnimationFrame(step);
  }, [controlsRef, stopZoomLoop, syncRef]);

  useEffect(
    () => () => {
      stopZoomLoop();
    },
    [stopZoomLoop],
  );

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      originYRef.current = e.clientY;
      movedRef.current = false;
      tapEligibleRef.current =
        (e.target as HTMLElement).closest("[data-nav-puck]") != null;
      setDragging(true);
      setDragOffset(0);
      speedRef.current = 0;
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      const rawOffset = e.clientY - originYRef.current;
      if (!movedRef.current && Math.abs(rawOffset) > TAP_THRESHOLD)
        movedRef.current = true;
      const clamped = Math.max(-PUCK_TRAVEL, Math.min(PUCK_TRAVEL, rawOffset));
      setDragOffset(clamped);
      const signedSpeed = speedFromDragOffset(clamped);
      speedRef.current = signedSpeed;
      if (Math.abs(signedSpeed) > 0) beginZoomLoop();
    },
    [beginZoomLoop, dragging],
  );

  const endGesture = useCallback(() => {
    setDragging(false);
    setDragOffset(0);
    const dragged = movedRef.current;
    movedRef.current = false;
    if (dragged) {
      tapEligibleRef.current = false;
      finishZoomGesture();
      return;
    }
    stopZoomLoop();
    if (tapEligibleRef.current) {
      const state = useStore.getState();
      setSingleDragPanEnabled(!state.singleDragPanEnabled);
      setOrbitEnabled(true, { toast: false });
    }
    tapEligibleRef.current = false;
  }, [
    finishZoomGesture,
    setOrbitEnabled,
    setSingleDragPanEnabled,
    stopZoomLoop,
  ]);

  const nudgeZoom = useCallback(
    (direction: 1 | -1) => {
      const controls = controlsRef.current;
      if (!controls) return;
      applyZoomDelta(direction * 0.42, controls, 0.11);
      syncRef.current?.();
    },
    [controlsRef, syncRef],
  );

  if (chromeHidden) return null;

  const trackSectionStyle: CSSProperties = {
    width: "100%",
    height: TRACK_SECTION_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    touchAction: "none",
  };

  const buttonGroupStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: PALETTE_BUTTON_GAP,
    flexShrink: 0,
  };

  const puckStyle: CSSProperties = {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: PUCK_SIZE,
    height: PUCK_SIZE,
    marginLeft: -PUCK_SIZE / 2,
    marginTop: -PUCK_SIZE / 2,
    transform: `translateY(${dragOffset}px)`,
    borderRadius: "50%",
    border: singleDragPanEnabled
      ? `1.5px solid ${COLOR_ACCENT}`
      : `1px solid ${COLOR_BORDER}`,
    background: singleDragPanEnabled ? COLOR_ACTIVE_BG : COLOR_HEADER_BG,
    color: singleDragPanEnabled ? COLOR_ACCENT : COLOR_TEXT_FAINT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: singleDragPanEnabled ? 1 : 0.8,
    transition: dragging
      ? "border-color 0.15s ease, color 0.15s ease, background 0.15s ease"
      : "transform 0.18s ease-out, border-color 0.15s ease, color 0.15s ease, background 0.15s ease",
    pointerEvents: "auto",
  };

  return (
    <ToolSurface edge="left">
      <EdgePalette
        id="viewport-camera"
        order={0}
        anchor="bottom"
        storageKey={CAMERA_PALETTE_STORAGE_KEY}
      >
        <div
          data-no-ctx
          style={trackSectionStyle}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
        >
          <div
            style={{
              position: "relative",
              width: EDGE_PALETTE_WIDTH,
              height: TRACK_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: TRACK_WIDTH,
                height: TRACK_HEIGHT,
                borderRadius: TRACK_WIDTH,
                background: COLOR_BORDER,
                opacity: 0.8,
              }}
            />
            <button
              type="button"
              aria-label="Zoom in"
              data-no-ctx
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                nudgeZoom(1);
              }}
              style={{
                position: "absolute",
                top: -12,
                left: "50%",
                width: 18,
                height: 18,
                marginLeft: -9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                border: `1px solid ${COLOR_BORDER}`,
                background: COLOR_HEADER_BG,
                color: COLOR_TEXT_FAINT,
                opacity: 0.7,
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Plus size={11} strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              data-no-ctx
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                nudgeZoom(-1);
              }}
              style={{
                position: "absolute",
                bottom: -12,
                left: "50%",
                width: 18,
                height: 18,
                marginLeft: -9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                border: `1px solid ${COLOR_BORDER}`,
                background: COLOR_HEADER_BG,
                color: COLOR_TEXT_FAINT,
                opacity: 0.7,
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Minus size={11} strokeWidth={2} />
            </button>
            <div
              data-nav-puck
              title={
                singleDragPanEnabled ? "Disable pan drag" : "Enable pan drag"
              }
              style={puckStyle}
            >
              <Hand size={13} strokeWidth={2.1} />
            </div>
          </div>
        </div>

        <ControlGroupSeparator />

        {showNavigationControls && (
          <>
            <div style={buttonGroupStyle}>
              <Tool
                ref={navButtonRef}
                icon={<NavigateIcon size={TOOL_ICON_SIZE} />}
                tip="Navigate (toggle orbit)"
                active={orbitEnabled !== spaceOrbitActive}
                onActivate={() => setOrbitEnabled(!orbitEnabled)}
              />
              <PrecisionControlButton />
            </div>
            <ControlGroupSeparator />
          </>
        )}

        <div style={buttonGroupStyle}>
          <CameraControlButton />
        </div>

        <div style={{ height: PALETTE_BOTTOM_PADDING, flexShrink: 0 }} />
      </EdgePalette>
    </ToolSurface>
  );
}
